import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import LoginMenu from '@apps/header-shared-multilabel/LoginMenu';
import { ProfileMenuLink } from '@apps/header-shared-multilabel/ProfileMenuLink';
import usePlaceholderComponentContent from '@apps/header-shared-multilabel/usePlaceholderComponentContent';
import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { getEnecoBeXapiSiteApiV1Profile } from '@eneco/dc-be/client';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { NavLink } from '@eneco/sparky';
import { ProfileIcon, SpinnerIcon } from '@eneco/sparky/icons';
import { MainMenuRightRendering } from '@sitecore/types/manual/NavigationBar';

type LoginLinkProps = {
  isMinimalHeader: boolean;
  isMyZone: boolean;
  setIsProfileMenuOpen: Dispatch<SetStateAction<boolean>>;
};

const LoginLink: FC<LoginLinkProps> = ({ isMinimalHeader, isMyZone, setIsProfileMenuOpen }) => {
  const content = usePlaceholderComponentContent<MainMenuRightRendering>('jss-main-navigation-right', 'LoginMenu');
  const { selectedAccount, setEmptyAccount } = useSelfServiceAccount() ?? {};
  const { data: session } = useSession() ?? {};
  const [displayName, setDisplayName] = useState(selectedAccount?.alias ?? '');

  const {
    data,
    isLoading: isGetProfileLoading,
    error: isGetProfileError,
  } = useAuthenticatedDCBE(getEnecoBeXapiSiteApiV1Profile, {}, ['session'], session);

  const getProfileData = unwrapData(data);

  useEffect(() => {
    setDisplayName(selectedAccount?.alias ?? '');
  }, [selectedAccount?.alias]);

  useEffect(
    function switchToProspect() {
      const name = getProfileData?.displayName || '';
      const isAlreadyEmptyAccount =
        selectedAccount?.crmAccountNumber === '' &&
        selectedAccount?.alias === name &&
        selectedAccount?.role === 'owner';

      if (
        !isGetProfileLoading &&
        !isGetProfileError &&
        getProfileData &&
        !selectedAccount?.crmAccountNumber &&
        !isAlreadyEmptyAccount
      ) {
        setDisplayName(name);
        setEmptyAccount?.(name);
      }
    },
    [getProfileData, isGetProfileLoading, isGetProfileError, selectedAccount?.alias, setEmptyAccount, selectedAccount],
  );

  if (isMinimalHeader) {
    return null;
  }

  const selfServiceLink = content?.fields.selfServiceLink.value;

  if (selfServiceLink?.linktype === 'external') {
    // Prevent MyZone from logging in the user when navigating to routes such as /business.
    return (
      <NavLink href={selfServiceLink.href} leftIcon={<ProfileIcon />}>
        {selfServiceLink.text}
      </NavLink>
    );
  }

  if (isGetProfileLoading) {
    return <SpinnerIcon />;
  }

  if (isMyZone) {
    return <ProfileMenuLink isLoading={false} displayName={displayName} onOpen={() => setIsProfileMenuOpen(true)} />;
  }

  if (session) {
    return (
      <NavLink href={selfServiceLink?.href} leftIcon={<ProfileIcon />}>
        {selfServiceLink?.text}
      </NavLink>
    );
  }

  return <LoginMenu />;
};

export default LoginLink;
